import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Pagination from '../components/Pagination';
import { logsAPI, getImageUrl } from '../services/api';
import { formatDateTime } from '../utils/dateTime';
import './Logs.css';

interface OperationLog {
  _id: string;
  created_at: string;
  operation_type: string;
  status: string;
  duration_ms: number;
  organization_id: string;
  person_id?: string;
  request_meta?: any;
  result?: any;
  error?: any;
}

interface LogsResponse {
  items: OperationLog[];
  page: number;
  page_size: number;
  total: number;
  pages: number;
}

/**
 * Extrai a URL da imagem de referência do log conforme o tipo de operação.
 * - search: imagem query salva em /images/{query_image_id}
 * - image_add: imagem adicionada (result.image_url)
 */
function getLogImageUrl(log: OperationLog): string | null {
  const type = log.operation_type;

  if (type === 'search') {
    const explicitUrl =
      log.request_meta?.search_error_image_url ||
      log.error?.search_error_image_url ||
      log.request_meta?.query_image_url ||
      log.result?.query_image_url;
    if (explicitUrl) return explicitUrl;

    const qid =
      log.request_meta?.query_image_id ||
      log.result?.query_image_id;
    if (qid) return `/images/${qid}`;
  }

  if (type === 'image_add') {
    const url = log.result?.image_url;
    if (url) return url;
    const rejectedUrl = log.error?.rejected_image_url;
    if (rejectedUrl) return rejectedUrl;
  }

  return null;
}

/** Extrai o primeiro resultado de busca (porcentagem + key). */
function getTopSearchResult(log: OperationLog): { key: string; similarity: number; image_url: string | null } | null {
  if (log.operation_type !== 'search' || log.status !== 'success') return null;
  const results = log.result?.results;
  if (!Array.isArray(results) || results.length === 0) return null;
  const top = results[0];
  return { key: top.key, similarity: top.similarity_score, image_url: top.image_url || null };
}

/** Verifica se o log de search pode ser re-executado (tem query_image_id). */
function canRerunSearch(log: OperationLog): boolean {
  if (log.operation_type !== 'search') return false;
  const qid =
    log.request_meta?.query_image_id ||
    log.result?.query_image_id;
  return Boolean(qid);
}

/** Abre a página de busca carregando o resultado do log pela API. */
function openLogInSearch(log: OperationLog): void {
  window.open(`/search/${log.organization_id}?log_id=${log._id}`, '_blank');
}

function Logs() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [items, setItems] = useState<OperationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [operationType, setOperationType] = useState('');
  const [status, setStatus] = useState('');
  const [hasWarning, setHasWarning] = useState('');
  const [personKeyInput, setPersonKeyInput] = useState('');
  const [personKey, setPersonKey] = useState('');
  const [selectedLog, setSelectedLog] = useState<OperationLog | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const loadLogs = async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await logsAPI.list(
        organizationId,
        page,
        20,
        operationType || undefined,
        status || undefined,
        hasWarning || undefined,
        personKey || undefined,
      );
      const data = response.data as LogsResponse;
      setItems(data.items || []);
      setPages(data.pages || 1);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [organizationId, page, operationType, status, hasWarning, personKey]);

  useEffect(() => {
    setPage(1);
  }, [operationType, status, hasWarning, personKey]);

  // Debounce do filtro por key (evita chamada a cada tecla).
  useEffect(() => {
    const handle = setTimeout(() => {
      setPersonKey(personKeyInput.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [personKeyInput]);

  const openDetail = async (logId: string) => {
    if (!organizationId) return;
    try {
      const response = await logsAPI.get(organizationId, logId);
      setSelectedLog(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao carregar detalhe do log');
    }
  };

  return (
    <div className="logs-container">
      <div className="logs-header">
        <h1>Logs da Organização</h1>
      </div>

      <div className="logs-filters">
        <select value={operationType} onChange={(e) => setOperationType(e.target.value)}>
          <option value="">Todos os tipos</option>
          <option value="search">search</option>
          <option value="person_create">person_create</option>
          <option value="person_delete">person_delete</option>
          <option value="image_add">image_add</option>
          <option value="image_remove">image_remove</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="success">success</option>
          <option value="error">error</option>
        </select>
        <select value={hasWarning} onChange={(e) => setHasWarning(e.target.value)} title="Filtrar por warnings">
          <option value="">Todos</option>
          <option value="yes">Com warning</option>
          <option value="no">Sem warning</option>
        </select>
        <div className="logs-search-key">
          <input
            type="text"
            className="logs-search-input"
            placeholder="Buscar por key da pessoa..."
            value={personKeyInput}
            onChange={(e) => setPersonKeyInput(e.target.value)}
            title="Filtra logs em que a key aparece como pessoa do evento ou top-1 de busca"
          />
          {personKeyInput && (
            <button
              type="button"
              className="logs-search-clear"
              onClick={() => setPersonKeyInput('')}
              title="Limpar busca"
            >
              ×
            </button>
          )}
        </div>
        <button className="logs-refresh-btn" onClick={loadLogs} title="Atualizar">🔄</button>
      </div>

      {error && <div className="logs-error">❌ {error}</div>}

      {loading ? (
        <p>Carregando logs...</p>
      ) : (
        <div className="logs-table-wrapper">
          <table className="logs-table">
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Imagem</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Resultado</th>
                <th>Erro/Warning</th>
                <th>Duração</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="logs-empty">Nenhum log encontrado</td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item._id}>
                    <td>{formatDateTime(item.created_at)}</td>
                    <td className="logs-thumb-cell">
                      {(() => {
                        const imgUrl = getLogImageUrl(item);
                        const spoof = item.result?.spoof_check_score;
                        const hasContent = imgUrl || spoof != null;
                        if (!hasContent) return <span className="logs-thumb-empty">—</span>;
                        return (
                          <span className="logs-thumb-with-spoof">
                            {imgUrl ? (
                              <img
                                className="logs-thumb"
                                src={getImageUrl(imgUrl)}
                                alt=""
                                onClick={() => setPreviewImage(getImageUrl(imgUrl))}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            ) : null}
                            {spoof != null && (
                              <span className="logs-spoof-cell">🛡️ {Number(spoof).toFixed(1)}%</span>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td>{item.operation_type}</td>
                    <td>
                      <span className={`logs-status ${item.status}`}>{item.status}</span>
                    </td>
                    <td>
                      {(() => {
                        const top = getTopSearchResult(item);
                        if (top) {
                          return (
                            <span className="logs-result-info">
                              {top.image_url && (
                                <img
                                  className="logs-thumb logs-result-thumb"
                                  src={getImageUrl(top.image_url)}
                                  alt=""
                                  onClick={() => setPreviewImage(getImageUrl(top.image_url!))}
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              )}
                              <span className="logs-result-text">
                                <strong>{(top.similarity * 100).toFixed(1)}%</strong>
                                <span className="logs-result-key">{top.key}</span>
                              </span>
                            </span>
                          );
                        }
                        if (item.operation_type === 'person_create' || item.operation_type === 'image_add' || item.operation_type === 'image_remove' || item.operation_type === 'person_delete') {
                          const personKey = item.request_meta?.person_key || item.result?.person_key;
                          if (personKey) {
                            return (
                              <span className="logs-result-text">
                                <span className="logs-result-key">{personKey}</span>
                              </span>
                            );
                          }
                        }
                        return <span className="logs-thumb-empty">—</span>;
                      })()}
                    </td>
                    <td>
                      {(() => {
                        const parts: React.ReactNode[] = [];
                        if (item.error) {
                          const msg = typeof item.error === 'string' ? item.error : item.error?.detail || item.error?.message || JSON.stringify(item.error);
                          parts.push(<span key="err" className="logs-error-cell" title={msg}>❌ {msg}</span>);
                        }
                        const warn = item.result?.warning;
                        const warnList = item.result?.warnings;
                        if (Array.isArray(warnList) && warnList.length > 0) {
                          const msg = warnList.map((w: any) => typeof w === 'string' ? w : (w?.message || JSON.stringify(w))).join('; ');
                          parts.push(<span key="warns" className="logs-warning-cell" title={msg}>⚠️ {msg}</span>);
                        } else if (warn) {
                          const msg = typeof warn === 'string' ? warn : warn?.message || warn?.error_type || JSON.stringify(warn);
                          parts.push(<span key="warn" className="logs-warning-cell" title={msg}>⚠️ {msg}</span>);
                        }
                        return parts.length > 0 ? <div className="logs-errwarn-stack">{parts}</div> : <span className="logs-thumb-empty">—</span>;
                      })()}
                    </td>
                    <td>{Number(item.duration_ms || 0).toFixed(2)} ms</td>
                    <td>
                      <div className="logs-actions">
                        <button
                          className="logs-detail-btn logs-icon-btn"
                          onClick={() => openDetail(item._id)}
                          title="Ver resultado"
                        >
                          📋
                        </button>
                        {canRerunSearch(item) && (
                          <button
                            className="logs-detail-btn logs-icon-btn"
                            onClick={() => openLogInSearch(item)}
                            title="Rever busca em nova aba"
                          >
                            🔁
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination currentPage={page} totalPages={pages} onPageChange={setPage} />

      {selectedLog && (
        <div className="logs-modal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="logs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logs-modal-header">
              <h3>Detalhe do Log</h3>
              <button className="logs-modal-close" onClick={() => setSelectedLog(null)}>&times;</button>
            </div>
            <pre>{JSON.stringify(selectedLog, null, 2)}</pre>
          </div>
        </div>
      )}

      {previewImage && (
        <div className="logs-modal-overlay" onClick={() => setPreviewImage(null)}>
          <div className="logs-image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logs-modal-header">
              <h3>Imagem</h3>
              <button className="logs-modal-close" onClick={() => setPreviewImage(null)}>&times;</button>
            </div>
            <div className="logs-image-wrapper">
              <img
                src={previewImage}
                alt="Imagem da requisição"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).parentElement!.innerHTML =
                    '<p style="padding:20px;color:#6b7280;">Imagem não disponível</p>';
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Logs;
